import React, { useMemo, useState } from 'react';
import {
  CustomizeTableColumnsIcon,
  CustomizeTableColumnsModal,
  timeSheetColumnIds,
  useTimeSheetColumnSettings,
} from '../../../components/TimeSheetsTable/CustomizeTableColumns';
import { useTimeEntryControllingExtraColumns } from './useTimeEntryControllingExtraColumns';

type Props = {
  isTimeBalanceEnabled: boolean;
};

export const useManagerTimeSheetColumnSettings = ({
  isTimeBalanceEnabled,
}: Props) => {
  const { extraColumns, loading } = useTimeEntryControllingExtraColumns();

  const supportedColumns = new Set<string>(timeSheetColumnIds);
  if (!isTimeBalanceEnabled) {
    supportedColumns.delete('timeBalanceAccumulatedMinutes');
  }
  extraColumns.forEach((column) => supportedColumns.add(column.id));

  const [settings, setSettings] = useTimeSheetColumnSettings(
    'timeSheets_columns',
    supportedColumns,
    extraColumns,
    loading,
  );

  return [settings, setSettings, extraColumns, loading] as const;
};

export const ManagerCustomizeTableColumnsIcon: React.FC<
  React.PropsWithChildren<Props>
> = ({ isTimeBalanceEnabled }) => {
  const [visible, setVisible] = useState<boolean>(false);

  const [
    timeSheetColumnSettings,
    setTimeSheetColumnSettings,
    extraColumns,
    loading,
  ] = useManagerTimeSheetColumnSettings({ isTimeBalanceEnabled });

  const extraColumnTitles = useMemo(
    () =>
      Object.fromEntries(
        extraColumns.map((column) => [column.id, column.title]),
      ),
    [extraColumns],
  );

  return (
    <>
      <CustomizeTableColumnsIcon onClick={() => setVisible(true)} />
      {/* Only mount the modal once extra-field metadata has loaded: it seeds its
          toggle list once and does not resync, so mounting it before the dynamic
          columns resolve would let a save drop the manager's enabled extras. */}
      {!loading && (
        <CustomizeTableColumnsModal
          visible={visible}
          timeSheetColumnSettings={timeSheetColumnSettings}
          extraColumnTitles={extraColumnTitles}
          onSave={setTimeSheetColumnSettings}
          onClose={() => setVisible(false)}
        />
      )}
    </>
  );
};
